/**
 * Terminal renderer for agent events — compact, polished output.
 *
 * Design: one-line tool summaries, in-place status line, line-numbered diffs,
 * markdown text rendering, thinking timer, and cost display.
 */

import chalk from 'chalk'
import type { AgentEvent } from '@spacecode/shared/agent'
import { renderMarkdown } from './markdown.js'

// ── State ───────────────────────────────────────────────────────────────────

type RenderPhase = 'idle' | 'thinking' | 'streaming' | 'tool_running'

let phase: RenderPhase = 'idle'
let toolCount = 0
let turnStartTime = Date.now()
let thinkingStart = 0
let thinkingTimer: ReturnType<typeof setInterval> | null = null
let pendingTools = new Map<string, { tool: string; input: Record<string, unknown>; start: number }>()
let totalInputTokens = 0
let totalOutputTokens = 0

export function resetTurnState(): void {
  phase = 'idle'
  toolCount = 0
  turnStartTime = Date.now()
  thinkingStart = 0
  stopThinkingTimer()
  pendingTools.clear()
  totalInputTokens = 0
  totalOutputTokens = 0
}

// ── Status Line ─────────────────────────────────────────────────────────────

function clearLine(): void {
  process.stderr.write('\r\x1b[K')
}

function statusLine(text: string): void {
  clearLine()
  process.stderr.write(chalk.dim(text))
}

// ── Thinking Timer ──────────────────────────────────────────────────────────

function startThinkingTimer(): void {
  thinkingStart = Date.now()
  phase = 'thinking'
  updateThinking()
  thinkingTimer = setInterval(updateThinking, 100)
}

function updateThinking(): void {
  const elapsed = ((Date.now() - thinkingStart) / 1000).toFixed(1)
  statusLine(`⏳ Thinking... (${elapsed}s)`)
}

function stopThinkingTimer(): void {
  if (thinkingTimer) {
    clearInterval(thinkingTimer)
    thinkingTimer = null
  }
}

// ── Diff Rendering ──────────────────────────────────────────────────────────

function renderDiff(oldStr: string, newStr: string): string {
  const oldLines = oldStr.split('\n')
  const newLines = newStr.split('\n')

  let prefixLen = 0
  while (prefixLen < oldLines.length && prefixLen < newLines.length && oldLines[prefixLen] === newLines[prefixLen]) {
    prefixLen++
  }

  let suffixLen = 0
  while (
    suffixLen < oldLines.length - prefixLen &&
    suffixLen < newLines.length - prefixLen &&
    oldLines[oldLines.length - 1 - suffixLen] === newLines[newLines.length - 1 - suffixLen]
  ) {
    suffixLen++
  }

  const result: string[] = []
  const maxLines = 15
  let lineCount = 0

  for (let i = prefixLen; i < oldLines.length - suffixLen && lineCount < maxLines; i++) {
    const lineNum = chalk.dim(`${String(i + 1).padStart(4)} │ `)
    result.push(`    ${lineNum}${chalk.red(`- ${oldLines[i]}`)}`)
    lineCount++
  }

  for (let i = prefixLen; i < newLines.length - suffixLen && lineCount < maxLines; i++) {
    const lineNum = chalk.dim(`${String(i + 1).padStart(4)} │ `)
    result.push(`    ${lineNum}${chalk.green(`+ ${newLines[i]}`)}`)
    lineCount++
  }

  const totalChanges = (oldLines.length - suffixLen - prefixLen) + (newLines.length - suffixLen - prefixLen)
  if (totalChanges > maxLines) {
    result.push(chalk.dim(`    ... ${totalChanges - maxLines} more lines`))
  }

  return result.join('\n')
}

// ── Tool Summary Formatting ─────────────────────────────────────────────────

function formatToolSummary(tool: string, input: Record<string, unknown>, result?: string, durationMs?: number): string {
  const duration = durationMs ? chalk.dim(` (${durationMs >= 1000 ? `${(durationMs / 1000).toFixed(1)}s` : `${durationMs}ms`})`) : ''

  switch (tool) {
    case 'read_file':
      return `  ${chalk.dim('●')} ${chalk.cyan('read_file')} ${input.path}${duration}`
    case 'write_file': {
      const content = String(input.content ?? '')
      const lines = content.split('\n').length
      return `  ${chalk.green('+')} ${String(input.path)} ${chalk.dim(`(${lines} lines)`)}`
    }
    case 'edit_file': {
      const oldLines = String(input.old_string ?? '').split('\n').length
      const newLines = String(input.new_string ?? '').split('\n').length
      const added = Math.max(0, newLines - oldLines)
      const removed = Math.max(0, oldLines - newLines)
      const changeSummary = added > 0 && removed > 0 ? `+${added} -${removed}` : added > 0 ? `+${added}` : removed > 0 ? `-${removed}` : '~'
      return `  ${chalk.dim('●')} ${chalk.yellow('edit_file')} ${input.path} ${chalk.dim(`(${changeSummary})`)}`
    }
    case 'bash': {
      const cmd = String(input.command ?? '').slice(0, 60)
      const summary = result ? extractBashSummary(result) : ''
      return `  ${chalk.dim('●')} ${chalk.magenta('bash')} ${cmd}${summary ? ` — ${summary}` : ''}${duration}`
    }
    case 'search': {
      const query = String(input.query ?? '')
      const count = result ? (result.match(/\n/g) ?? []).length : 0
      return `  ${chalk.dim('●')} ${chalk.blueBright('search')} "${query}" — ${count} results${duration}`
    }
    case 'list_directory': {
      const count = result ? result.split('\n').filter(Boolean).length : 0
      return `  ${chalk.dim('●')} ${chalk.blue('list_directory')} ${input.path} — ${count} items${duration}`
    }
    case 'build_feature':
      // Invisible — individual file_changed events cover the output
      return ''
    case 'create_pull_request': {
      const title = String(input.title ?? '')
      const prUrl = result ? result.trim() : ''
      const urlLine = prUrl ? `\n    ${chalk.dim('→')} ${chalk.underline(prUrl)}` : ''
      return `  ${chalk.dim('●')} ${chalk.green('create_pull_request')} ${title}${duration}${urlLine}`
    }
    case 'preview': {
      const url = result?.match(/https?:\/\/\S+/)?.[0] ?? null
      const isReady = result?.startsWith('Preview ready') ?? false
      const icon = isReady ? chalk.green('▶') : chalk.yellow('◐')
      const label = isReady ? chalk.green.bold('Preview ready') : chalk.yellow.bold('Preview starting')
      const urlLine = url ? `\n    ${chalk.dim('→')} ${chalk.cyan.underline(url)}` : ''
      return `  ${icon} ${label}${duration}${urlLine}`
    }
    default:
      return `  ${chalk.dim('●')} ${tool}${duration}`
  }
}

function extractBashSummary(output: string): string {
  const lines = output.trim().split('\n')
  const last = lines[lines.length - 1] ?? ''

  // Test summary patterns
  const testMatch = output.match(/(\d+) (?:tests? )?passed/i)
  if (testMatch) return chalk.green(`${testMatch[1]} passed`)

  const failMatch = output.match(/(\d+) (?:tests? )?failed/i)
  if (failMatch) return chalk.red(`${failMatch[1]} failed`)

  if (/success|✓|done/i.test(last)) return chalk.green('success')

  return ''
}

function formatToolError(tool: string, input: Record<string, unknown>, output: string, durationMs?: number): string {
  const cmd = tool === 'bash' ? String(input.command ?? '').slice(0, 60) : String(input.path ?? tool)
  const duration = durationMs ? chalk.dim(` (${(durationMs / 1000).toFixed(1)}s)`) : ''

  const lines = output.split('\n').filter(Boolean)
  const maxLines = 8
  const truncated = lines.length > maxLines
  const shown = lines.slice(0, maxLines)

  const result = [`  ${chalk.red('✗')} ${tool} ${cmd}${duration}`]
  for (const line of shown) {
    result.push(`    ${chalk.dim('│')} ${line}`)
  }
  if (truncated) {
    result.push(chalk.dim(`    │ ... ${lines.length - maxLines} more`))
  }

  return result.join('\n')
}

// ── Cost Estimation ─────────────────────────────────────────────────────────

const COST_PER_M_INPUT = 3   // $/M tokens (Sonnet-class)
const COST_PER_M_OUTPUT = 15

function estimateCost(inputTokens: number, outputTokens: number): string {
  const cost = (inputTokens * COST_PER_M_INPUT + outputTokens * COST_PER_M_OUTPUT) / 1_000_000
  return `$${cost.toFixed(cost < 0.01 ? 4 : 2)}`
}

// ── Main Render ─────────────────────────────────────────────────────────────

export function renderEvent(event: AgentEvent): void {
  switch (event.type) {
    case 'status': {
      if (event.phase === 'thinking' && phase !== 'thinking') {
        startThinkingTimer()
      }
      break
    }

    case 'text': {
      // Full text — skip (text_delta handles streaming)
      break
    }

    case 'text_delta': {
      if (phase === 'thinking') {
        stopThinkingTimer()
        clearLine()
        process.stderr.write('\n')
      }
      phase = 'streaming'
      // Write raw — text_delta is character/word fragments, markdown parsing
      // would break across chunk boundaries
      process.stdout.write(event.text)
      break
    }

    case 'tool_call': {
      if (phase === 'thinking') {
        stopThinkingTimer()
        clearLine()
        process.stderr.write('\n')
      }
      phase = 'tool_running'
      toolCount++
      pendingTools.set(event.id, { tool: event.tool, input: event.input, start: Date.now() })

      // Show what's running in status line
      const inputSummary = event.tool === 'bash'
        ? String(event.input.command ?? '').slice(0, 40)
        : String(event.input.path ?? event.tool)
      statusLine(`🔧 ${event.tool}: ${inputSummary} │ ${toolCount} tools`)
      break
    }

    case 'tool_output': {
      // Update status line with latest output for long-running tools
      const pending = pendingTools.get(event.id)
      if (pending) {
        const elapsed = Date.now() - pending.start
        if (elapsed > 5000) {
          const lastLine = event.text.trim().split('\n').pop() ?? ''
          statusLine(`🔧 ${pending.tool}: ${lastLine.slice(0, 50)} │ ${toolCount} tools`)
        }
      }
      break
    }

    case 'tool_result': {
      clearLine()
      const pending = pendingTools.get(event.id)
      pendingTools.delete(event.id)

      if (!pending) break

      const durationMs = event.duration_ms ?? (Date.now() - pending.start)

      if (event.is_error) {
        process.stderr.write(formatToolError(pending.tool, pending.input, event.output, durationMs) + '\n')
      } else {
        const summary = formatToolSummary(pending.tool, pending.input, event.output, durationMs)
        if (summary) process.stderr.write(summary + '\n')

        // Show inline diff for edit_file
        if (
          pending.tool === 'edit_file' &&
          typeof pending.input.old_string === 'string' &&
          typeof pending.input.new_string === 'string'
        ) {
          const diff = renderDiff(pending.input.old_string, pending.input.new_string)
          if (diff) process.stderr.write(diff + '\n')
        }
      }

      phase = pendingTools.size > 0 ? 'tool_running' : 'idle'
      break
    }

    case 'file_changed': {
      const icon = event.status === 'created' ? chalk.green('+') : event.status === 'deleted' ? chalk.red('-') : chalk.yellow('~')
      const lines = event.content.split('\n').length
      process.stderr.write(`  ${icon} ${event.path} ${chalk.dim(`(${lines} lines)`)}\n`)
      break
    }

    case 'done': {
      stopThinkingTimer()
      clearLine()

      const elapsed = ((Date.now() - turnStartTime) / 1000).toFixed(1)
      const toolLabel = toolCount === 1 ? '1 tool' : `${toolCount} tools`

      // Token display — use what's available
      let tokenInfo = ''
      if (totalInputTokens > 0 || totalOutputTokens > 0) {
        const cost = estimateCost(totalInputTokens, totalOutputTokens)
        tokenInfo = ` │ ${totalInputTokens.toLocaleString()} → ${totalOutputTokens.toLocaleString()} tokens │ ${cost}`
      }

      if (event.success) {
        process.stderr.write(chalk.green(`\n✓ Done (${toolLabel}, ${elapsed}s)${tokenInfo}\n`))
      } else {
        process.stderr.write(chalk.red(`\n✗ Failed (${toolLabel}, ${elapsed}s)${tokenInfo}\n`))
      }

      // Turn separator
      process.stderr.write(chalk.dim('─'.repeat(41)) + '\n')
      phase = 'idle'
      break
    }

    case 'result': {
      clearLine()
      process.stdout.write(renderMarkdown(event.text))
      if (!event.text.endsWith('\n')) process.stdout.write('\n')
      break
    }

    case 'error': {
      stopThinkingTimer()
      clearLine()
      process.stderr.write(`  ${chalk.red('✗')} ${chalk.red(event.message)}\n`)
      break
    }
  }
}

/**
 * Track token usage from API responses.
 * Called by the agent client when it receives usage data.
 */
export function trackTokens(inputTokens: number, outputTokens: number): void {
  totalInputTokens += inputTokens
  totalOutputTokens += outputTokens
}
